import Router from 'next/router';
import { camelizeKeys } from '@zoocasa/node-kit/objects/transform-keys';
import { Point, getListingById } from 'data/listing';
import { dasherize } from '@zoocasa/node-kit/strings/dasherize';
import getTargetedUrl from 'utils/targeted-url-helper';
import { captureLastSearch } from 'pages/api/last-search/lastSearchApiClient';
import { formatQueryIntoURLQueryParams } from 'utils/listing-query-helper';
import defaultListingParams, { DEFAULT_LISTING_PARAMS_US } from 'contexts/preferences/listing-params/defaults';
import { findLocationByPlaceId } from 'utils/google-maps/geoLocator';
import { trackEvent } from 'utils/google-tag-manager';
import determineZoomLevel from 'utils/determine-zoom-level';
import { AutocompletePredictionPredictionCategory, SearchPredictionSource } from 'utils/google-maps/types';
import { Position } from '@zoocasa/node-kit/map/types';
import { ThemeNames } from 'types/themes';
import { getGoSearchHost, isServerSide } from 'utils/host-config';
import { DEFAULT_HAS_IMAGE_VALUE } from 'contexts/preferences/listing-params';
import { MILITARY_BASE_ZOOM_LEVEL } from 'utils/determine-zoom-level';
import configJSON from 'config.json';
import { isCanadianProvinceCode, ProvinceOrState, provinceOrStateCodeFromName, provinceOrStateCodeFromSlug } from 'utils/province_or_state';
import { LastSearchLocation } from 'contexts/preferences';
import { getPositionFromAddress } from 'utils/google-maps/api/geocoder';
import { SearchSuggestions } from 'components/suggested-location-dropdown';
import { getCoordinatesForLocation } from 'utils/user-location';
import { cleanSlug } from 'utils/google-maps/zoopraisal';

import type { FeaturesType } from 'contexts/features';
import type ListingParams from 'contexts/preferences/listing-params';
import { SearchApi } from 'data/search/api';
import { SearchPredictionsParams, GoSearchPredictionData } from 'data/search/predictions/types';
import { capitalizeWords } from '@zoocasa/node-kit/strings';
import { getAddressesBySlug } from 'data/addresses/go-search/fetcher';
import { UNSUPPORTED_ADDRESS_TYPES } from 'data/addresses';
export type MapType = { changeLocation(location: Position, zoom: number): void };

export const LOCATION_ID_PREFIX = 'location-';

export const LISTINGS_SEARCH_PREDICTION_GROUP = 'listings';
export const LOCATIONS_SEARCH_PREDICTION_GROUP = 'locations';
export const SCHOOLS_SEARCH_PREDICTION_GROUP = 'schools';
export const BUILDINGS_SEARCH_PREDICTION_GROUP = 'buildings';
export const MILITARY_BASES_SEARCH_PREDICTION_GROUP = 'militaryBases';
export type SearchPredictionGroup = typeof LISTINGS_SEARCH_PREDICTION_GROUP | typeof LOCATIONS_SEARCH_PREDICTION_GROUP | typeof SCHOOLS_SEARCH_PREDICTION_GROUP | typeof BUILDINGS_SEARCH_PREDICTION_GROUP | typeof MILITARY_BASES_SEARCH_PREDICTION_GROUP;

export default class SearchPrediction {
  id: string;
  locationId: string | null;
  description: string;
  group: SearchPredictionGroup;
  label: AutocompletePredictionPredictionCategory | null;
  matchedSubstrings: {
    length: number;
    offset: number;
  }[];
  data: {
    position: Point;
  } | null;
  // TODO: change to SearchPredictionSource
  source: string | null;
  identifier: number | null;
  urlPath?: string;

  // Add static features property to store feature flags
  static features: FeaturesType | null = null;

  // Original constructor was design to accept search prediction data from old client-app api
  // With Ruby type object like (attributes: { ... }).
  // Go-Search still returns such object as the legacy, but will be migrated the new format with proto models.
  // So we need to handle both cases for now until we migrate all calls to use new constructor.
  constructor(searchPrediction: Record<string, unknown>);
  constructor(data: GoSearchPredictionData, source: SearchPredictionSource);
  constructor(searchPrediction: Record<string, unknown> | GoSearchPredictionData, source?: SearchPredictionSource.GO_SEARCH) {
    let processedData: Record<string, unknown>;

    if (source === SearchPredictionSource.GO_SEARCH) {
      // Handle go-search data
      const camelizedPrediction = camelizeKeys(searchPrediction as unknown as Record<string, unknown>);

      // Set the source in the attributes object
      if (camelizedPrediction.attributes) {
        (camelizedPrediction.attributes as Record<string, unknown>).source = SearchPredictionSource.GO_SEARCH;
      }

      processedData = camelizedPrediction;
      processedData.source = SearchPredictionSource.GO_SEARCH;
    } else if (source === SearchPredictionSource.GOOGLE) {
      // TODO Create a new constructor for google data and drop original constructor
    } else {
      // Handle generic data (existing behavior)
      processedData = searchPrediction as Record<string, unknown>;
    }

    const camelizedSearchPrediction = camelizeKeys(processedData);
    const attributes = camelizedSearchPrediction.attributes as Record<string, unknown>;
    const relationships = camelizedSearchPrediction.relationships as Record<
      string,
      unknown
    >;
    const formattedSearchPrediction = {
      ...attributes,
      ...relationships,
      id: camelizedSearchPrediction.id,
    } as SearchPrediction;
    Object.assign(this, formattedSearchPrediction);
  }

  /**
   * Sets the features from request headers or other sources
   * @param features Feature flags to use for path determination
   */
  static setFeatures(features: FeaturesType) {
    SearchPrediction.features = features;
  }

  // TODO: Validate this function is being used somewhere useful, couldn't find it right away
  // The only place I found it being used is in the transitionToPath function with buildings.
  // As far I know, buildings are not supported at the moment, so we can remove this function probably.
  get path() {
    const { group } = this;
    if (group === LOCATIONS_SEARCH_PREDICTION_GROUP) {
      const [longitude, latitude] = this.data?.position ? this.data!.position.coordinates : [0, 0];
      return '/search?' + formatQueryIntoURLQueryParams({ latitude, longitude });
    } else if (group === MILITARY_BASES_SEARCH_PREDICTION_GROUP) {
      // Use static features property if available, otherwise fall back to config.json
      let routeMilitaryBaseToAreaPage: boolean;

      if (SearchPrediction.features) {
        routeMilitaryBaseToAreaPage = SearchPrediction.features.routeMilitaryBaseToAreaPage;
      } else {
        const config = configJSON;
        routeMilitaryBaseToAreaPage = config.features.routeMilitaryBaseToAreaPage;
      }

      if (routeMilitaryBaseToAreaPage) {
        // For military bases, the ID is already in the format <slug>-<military-branch>
        return `/${this.id}`;
      } else {
        // Use the search route with the military base slug as location
        const [longitude, latitude] = this.data?.position ? this.data!.position.coordinates : [0, 0];
        return '/search?' + formatQueryIntoURLQueryParams({
          latitude,
          longitude,
          'area-name': this.description,
        });
      }
    } else {
      const typeOfPrediction = group.replace(`${group}-`, '').replace(/s$/, '');
      const slug = this.id.replace(/\s$/, '').replace(`${typeOfPrediction}-`, '');
      return `/${typeOfPrediction}/${slug}`;
    }
  }

  get slug() {
    return createSlug(this);
  }

  setIdentifier = (identifier: number) => {
    this.identifier = identifier;
  };


  /**
   * Handles navigation when a search prediction is selected.
   *
   * This function processes predictions in the following order:
   * 1. **Coordinate Processing**: Validates/fetches coordinates for the prediction
   * 2. **Recent Searches**: Adds prediction to recent searches (if valid coordinates)
   * 3. **Last Search Tracking**: Saves search parameters for the user
   * 4. **Navigation Decision**: Determines where to navigate based on prediction source and type
   *
   * **Navigation Logic**:
   * - **Go-Search with urlPath**: Uses provided URL with applied filters
   * - **Google/Go-Search with slug**: Navigates to area page with filters
   * - **No slug**: Falls back to coordinate-based search page
   * - **Other Types**: Handles listings, schools, buildings, military bases
   *
   * @param searchPageQueryParams - Current search parameters and filters
   * @param sourceTheme - Theme context for analytics and last search tracking
   * @param map - Optional map instance for coordinate updates (when staying on map page)
   * @param user - User object for last search persistence
   * @param addToRecentSearches - Optional callback to add prediction to recent searches
   *
   * @example
   * ```typescript
   * // Navigate to a go-search location prediction (processes coordinates first)
   * await prediction.transitionToPath(searchParams, 'zoocasa', undefined, user, addToRecent);
   *
   * // Update map coordinates without navigation
   * await prediction.transitionToPath(searchParams, 'zoocasa', mapInstance, user);
   * ```
   */
  transitionToPath = async (
    searchPageQueryParams: ListingParams,
    sourceTheme: ThemeNames,
    map?: MapType,
    user?: any,
    addToRecentSearches?: (prediction: SearchPrediction) => void,
    forceAreapage = false
  ) => {
    const { group, source, urlPath } = this;
    trackEvent(group, 'search-results');
    if (group === LOCATIONS_SEARCH_PREDICTION_GROUP && searchPageQueryParams) {
      // Use existing coordinates if they're already set and valid
      let longitude = this.data?.position?.coordinates[0];
      let latitude = this.data?.position?.coordinates[1];
      const zoom = determineZoomLevel(this);
      let isUsingDefaultCoordinates = false;

      // Skip geocoding if we already have valid coordinates
      const hasValidCoordinates = typeof longitude === 'number' &&
                                  typeof latitude === 'number' &&
                                  !isNaN(longitude) && !isNaN(latitude) &&
                                  longitude !== 0 && latitude !== 0 &&
                                  longitude >= -180 && longitude <= 180 &&
                                  latitude >= -90 && latitude <= 90;

      if (!hasValidCoordinates) {
        // If we have a placeId from google maps, try to get coordinates from it
        if (this.locationId) {
          try {
            const location = await findLocationByPlaceId(this.locationId);
            if (location) {
              longitude = location.longitude;
              latitude = location.latitude;
            }
          } catch (error) {
            console.error('Error getting location by place ID:', error);
          }
        }

        // Try geocoding the address
        if (!longitude || !latitude) {
          try {
            const position = await getPositionFromAddress(this.description);
            if (position) {
              longitude = position.geometry.location.lng();
              latitude = position.geometry.location.lat();
              this.locationId = position.place_id;
            }
          } catch (error) {
            console.error('Error getting position from address:', error);
          }
        }

        // If we still don't have valid coordinates after all attempts, use defaults
        if (!latitude || !longitude) {
          isUsingDefaultCoordinates = true;
          // Get country code from the description if possible
          const description = this.description || '';
          if (description.includes('USA') || description.includes('United States') || description.includes('US')) {
            // US-based default
            latitude = DEFAULT_LISTING_PARAMS_US.filter.latitude;
            longitude = DEFAULT_LISTING_PARAMS_US.filter.longitude;
          } else {
            // Generic default
            latitude = defaultListingParams.filter.latitude;
            longitude = defaultListingParams.filter.longitude;
          }
        }

        // Update the data object with new coordinates
        this.data = {
          position: new Point({
            coordinates: [longitude, latitude],
            type: 'Point',
          }),
        };
      }

      if (typeof addToRecentSearches === 'function' && !isUsingDefaultCoordinates) {
        addToRecentSearches(this);
      }

      if (this.slug) {
        searchPageQueryParams.setSlug(this.slug);
      } else {
        searchPageQueryParams.setSlug('');
      }
      if (this.description) {
        const desc = this.description.replace('-', ' ');
        const normalizedDescription = capitalizeWords(desc);
        searchPageQueryParams.setAreaName(normalizedDescription);
      }
      searchPageQueryParams.setZoom(zoom);
      searchPageQueryParams.setLatitude(latitude);
      searchPageQueryParams.setLongitude(longitude);

      // Determine which page user should go to
      if (map && !forceAreapage) { // if on map page without hybrid page enabled, stay in map page
        // Update the listingParams with the new coordinates and zoom, and save them to the user's last search
        if (user?.id && searchPageQueryParams) {
          captureLastSearch({ userId: user.id, lastSearch: searchPageQueryParams, sourceTheme: sourceTheme });
        }
        map.changeLocation({ lng: longitude, lat: latitude }, zoom);
        return;
      } else { // if on other pages, determine if we should go to an area page or map page
        // If we received a province or state name from google, we will generate a custom slug for it
        const normalizedDescription = normalizeDescription(this.description);
        const dasherizedDescription = dasherize(normalizedDescription);
        const isProvinceOrState = provinceOrStateCodeFromSlug(dasherizedDescription);
        let customSlug;
        if (isProvinceOrState) customSlug = `${dasherizedDescription}-real-estate`;

        // Handle go-search predictions with provided URL paths
        if (source === SearchPredictionSource.GO_SEARCH && urlPath) {
          // Apply filters to the go-search URL if possible
          const urlWithFilters = getTargetedUrl({
            url: `${window.location.origin}${urlPath}`,
            filters: searchPageQueryParams.filter,
            sort: searchPageQueryParams.sort,
            pageNumber: 1,
          });
          Router.push(urlWithFilters);
        }
        // if location has a slug and is not a street go to area page with existing filters
        else if ((this.slug || customSlug) && this.label !== AutocompletePredictionPredictionCategory.STREET) {
          let location = this.slug;

          const slugToCheck = customSlug? dasherizedDescription : this.slug;
         
          const areaPageExists = await checkAreaPageExists(slugToCheck); 
          if (!areaPageExists){
            // if particular area page doesn't exist, go to search page with filters set
            routeToSearchPage(searchPageQueryParams, sourceTheme, user );
            return;
          }
          

          let subLocation = '';
          if (this.label === AutocompletePredictionPredictionCategory.NEIGHBORHOOD) {
            const locationArr = normalizedDescription.split(', ');
            subLocation = '/' + dasherize(locationArr[0]);
            location = dasherize((locationArr[1] + ' ' + locationArr[2]).toLowerCase());
          }
          // For google we created custom slug for province or state
          const areaPageUrl = customSlug ? `${window.location.origin}/${customSlug}` : `${window.location.origin}/${location}-real-estate${subLocation}`;
          const areaPageUrlWithFilters = getTargetedUrl({ url: areaPageUrl, filters: searchPageQueryParams.filter, sort: searchPageQueryParams.sort, pageNumber: 1 });
          if (user?.id && searchPageQueryParams) {
            captureLastSearch({ userId: user.id, lastSearch: searchPageQueryParams, sourceTheme: sourceTheme });
          }
          Router.push(areaPageUrlWithFilters);
        } else { // if location does not have a slug go to search page with existing filters
          routeToSearchPage( searchPageQueryParams, sourceTheme, user);
        }
      }
    } else if (group === LISTINGS_SEARCH_PREDICTION_GROUP) { // if search result is a listing
      const listingId = this.id.split('-')[1];
      const listing = await getListingById(listingId);
      Router.push(listing.addressPathWithFallback);
    } else if (group === SCHOOLS_SEARCH_PREDICTION_GROUP) { // if search result is a school
      window.location.href = `/school/${this.id.replace('school-', '')}`;
    } else if (group === BUILDINGS_SEARCH_PREDICTION_GROUP) { // if search result is a building
      Router.push(this.path);
    } else if (group === MILITARY_BASES_SEARCH_PREDICTION_GROUP) { // if search result is a military base
      // Add to recent searches if the function is provided
      if (typeof addToRecentSearches === 'function') {
        addToRecentSearches(this);
      }

      // Use static features property if available, otherwise fall back to config.json
      let routeMilitaryBaseToAreaPage: boolean;

      if (SearchPrediction.features) {
        routeMilitaryBaseToAreaPage = SearchPrediction.features.routeMilitaryBaseToAreaPage;
      } else {
        const config = configJSON;
        routeMilitaryBaseToAreaPage = config.features.routeMilitaryBaseToAreaPage;
      }

      if (routeMilitaryBaseToAreaPage) {
        // For military bases, the ID is already in the format <slug>-<military-branch>
        Router.push(`/${this.id}`);
      } else {
        // Use the search route with the military base slug as location
        const [longitude, latitude] = this.data?.position ? this.data!.position.coordinates : [0, 0];
        // Use the description in the format "base name - type - state code"
        const searchParams = {
          latitude,
          longitude,
          'area-name': this.description,
          zoom: MILITARY_BASE_ZOOM_LEVEL,
        };
        Router.push('/search?' + formatQueryIntoURLQueryParams(searchParams));
      }
    }
  };
}

// Fetch search predictions from Go-Search
export async function getSearchPredictions(params: Record<string, any>, features?: FeaturesType) {
  try {
    const searchApi = SearchApi.create(getGoSearchHost(isServerSide()));
    const searchPredictionsResponse = await searchApi.searchPredictions(params as SearchPredictionsParams);

    if (!searchPredictionsResponse) {
      return null;
    }

    // Set features if provided
    if (features) {
      SearchPrediction.setFeatures(features);
    }

    // Transform the raw API data into SearchPrediction objects using the constructor overload
    return searchPredictionsResponse.data
      .filter(rawPrediction => {
        // This is temp fix to handle data pollution from Go-Search, needs to be removed once we finish ticket
        // https://coda.io/d/Zoocasa-eXprealty-com_dT-0uNbKbyy/SD-Team_suGnAaTg#Priority_tuXsB_Nl/r1022&view=center
        return !['/alberta-ab-real-estate', '/nova-scotia-ns-real-estate'].includes(rawPrediction.attributes.url_path);
      })
      .map(rawPrediction => new SearchPrediction(rawPrediction, SearchPredictionSource.GO_SEARCH));
  } catch (error) {
    console.error('Error getting search predictions:', error);
    return null;
  }
}

/**
 * Check if an area page exists for the given slug (verify with go-search address api)
 * Validates both slug existence and that it's a valid area page type (excludes ADDRESS, UNRECOGNIZED, UNSPECIFIED types)
 */
const checkAreaPageExists = async (slug: string) => {
  try {
    const addressList = await getAddressesBySlug(slug);
    if (!addressList || addressList.length === 0) {
      return false;
    }
    
    // Get the most specific address
    // The address array is sorted from least to most specific
    const mostSpecificAddress = addressList[addressList.length - 1];
    if (!mostSpecificAddress?.addressType) {
      return false;
    }
    
    // Area page does not exist if the address type is unsupported (ie. specific street addresses)
    return !UNSUPPORTED_ADDRESS_TYPES.includes(mostSpecificAddress.addressType);
  }
  catch (error) {
    console.error(`Error validating the slug ${slug} `, error);
    return false;
  }
};

/**
 * Navigate to search page with existing filters/ sort set. Save the user's last search 
 */
const routeToSearchPage = (searchPageQueryParams: ListingParams, sourceTheme: ThemeNames, user?: any) => {

  if (user?.id && searchPageQueryParams) {
    captureLastSearch({ userId: user.id, lastSearch: searchPageQueryParams, sourceTheme: sourceTheme });
  }
  Router.push('/search?' + formatQueryIntoURLQueryParams(searchPageQueryParams.filter) + '&sort=' + searchPageQueryParams.sort);

};

/**
 * @deprecated This function is legacy technical debt and should be removed.
 *
 * It creates a SearchPrediction from an existing SearchPrediction object using the old Ruby-style
 * constructor format, which is redundant and unnecessary. The function:
 * - Reconstructs the data object identically
 * - Only keeps the first matchedSubstring (data loss)
 * - Uses the legacy attributes wrapper format
 *
 * TODO: Remove this function and replace all usages with:
 * - Direct SearchPrediction constructor calls for new instances
 * - Object cloning utilities if copying is needed
 * - The new constructor overloads for go-search/google data
 *
 * @param properties - SearchPrediction object to "recreate"
 * @returns New SearchPrediction instance (unnecessarily)
 */
export function createSearchPredictionModel(properties: SearchPrediction) {
  let data = null;
  if (properties.data) {
    data = { position: { ...properties.data.position, coordinates: [properties.data.position.coordinates[0], properties.data.position.coordinates[1]]}};
  }
  let matchedSubstrings: { length: number; offset: number }[] = [];
  if (properties.matchedSubstrings && properties.matchedSubstrings[0]) {
    matchedSubstrings = [{ length: properties.matchedSubstrings[0].length, offset: properties.matchedSubstrings[0].offset }];
  }

  return new SearchPrediction({
    attributes: {
      ...properties,
      data,
      matchedSubstrings,
    },
    id: properties.id,
  });
}

export function createSlug({ group, id, description, source }: Pick<SearchPrediction, 'group' | 'id' | 'description' | 'source'>) {
  let slug = '';
  if (source === SearchPredictionSource.GOOGLE) {
    const isJustProvinceOrState = description.split(',').length < 2;
    const normalizedDescription = !isJustProvinceOrState ? normalizeDescription(description) : description;
    const parts = normalizedDescription.split(',').map(part => part.trim().toLowerCase());
    slug = dasherize(parts.join(' '));
  } else if (group === 'locations') {
    slug = getLocationSlugFromId(id);
  }
  return cleanSlug(slug);
}

export function normalizeDescription(description: string) {
  // Split the description into components (typically format: "City, Province/State")
  const components = description.split(',').map(part => part.trim());
  // If we have fewer than 2 components, return the original description
  if (components.length < 2) {
    return description;
  }
  // Check if the second-to-last component is a province/state
  if (components.length >= 2) {
    const potentialProvinceOrState = components[components.length - 1];
    // Try to get the province/state code
    const provinceOrStateCode = provinceOrStateCodeFromName(potentialProvinceOrState as ProvinceOrState);
    if (provinceOrStateCode) {
      // Replace the province/state name with its code
      components[components.length - 1] = provinceOrStateCode.toUpperCase();
    }
  }
  // Rejoin the components
  return components.join(', ');
}

/**
 * Returns a slug parsed from a location id in a search prediction.
 *
 * NOTE: This can also determine whether this location has an area page.
 *
 * @param id the id of the search prediction {@link SearchPrediction.id}. Ids are of the format `location-<slug>`
 * @returns the slug parsed from the location id or undefined if no slug is found
 */
export function getLocationSlugFromId(id: string) { // This can also determine whether this location has an area page
  return id.split(LOCATION_ID_PREFIX)[1];
}

export function checkLocationHasAreaPageWithId(id: string) {
  return !!getLocationSlugFromId(id);
}

export type LastSearchUrlsResponse = {
  homesForSaleUrl: string;
  soldPricesUrl: string;
  mapViewUrl: string;
};

export type LastSearchSlugAndCoordsResponse = {
  slug?: string;
  latitude: number;
  longitude: number;
  subLocation?: string;
  locationId: string;
  dasherizedDescription: string;
};

export const generateSlugAndCoordinatesFromLastSearch = async (lastSearch: LastSearchLocation): Promise<LastSearchSlugAndCoordsResponse> => {
  let lastSearchLatitude = lastSearch?.data?.position?.latitude || lastSearch?.data?.position?.coordinates[1];
  let lastSearchLongitude = lastSearch?.data?.position?.longitude || lastSearch?.data?.position?.coordinates[0];
  const isMilitaryBase = lastSearch.group === MILITARY_BASES_SEARCH_PREDICTION_GROUP;

  const dasherizedDescription = dasherize(normalizeDescription(lastSearch.description).replaceAll(',', '').toLowerCase());
  const locationId = lastSearch.id.startsWith(LOCATION_ID_PREFIX) ? lastSearch.id : dasherizedDescription;
  const hasAreaPage = checkLocationHasAreaPageWithId(lastSearch.id);
  const isProvinceOrState = provinceOrStateCodeFromSlug(dasherizedDescription);

  if (isProvinceOrState) {
    return {
      slug: dasherizedDescription,
      latitude: lastSearchLatitude,
      longitude: lastSearchLongitude,
      locationId,
      dasherizedDescription,
    };
  }
  else if (hasAreaPage) {
    let location = getLocationSlugFromId(lastSearch.id);
    let subLocation = '';
    if (lastSearch.label === AutocompletePredictionPredictionCategory.NEIGHBORHOOD) {
      const locationArr = lastSearch.description.split(', ');
      subLocation = '/' + dasherize(locationArr[0]);
      location = dasherize((locationArr[1] + ' ' + locationArr[2]).toLowerCase());
    }
    const slug = isMilitaryBase ? lastSearch.id : location;
    return {
      slug,
      latitude: lastSearchLatitude,
      longitude: lastSearchLongitude,
      subLocation: subLocation,
      locationId,
      dasherizedDescription,
    };
  }
  else {
    if ((!lastSearchLongitude || !lastSearchLongitude) && lastSearch?.locationId) {
      const geoLocation = await findLocationByPlaceId(lastSearch.locationId);
      lastSearchLatitude = geoLocation?.latitude;
      lastSearchLongitude = geoLocation?.longitude;
    }
    return {
      latitude: lastSearchLatitude,
      longitude: lastSearchLongitude,
      locationId,
      dasherizedDescription,
    };
  }
};

export const generateUrlsFromLastSearch = async (lastSearch: LastSearchLocation, hideImagelessListings?: boolean): Promise<LastSearchUrlsResponse> => {
  const slugAndCoordinates = await generateSlugAndCoordinatesFromLastSearch(lastSearch);
  const hasAreaPage = checkLocationHasAreaPageWithId(slugAndCoordinates.locationId);
  const isProvinceOrState = provinceOrStateCodeFromSlug(slugAndCoordinates.dasherizedDescription);
  const imageQuery = hideImagelessListings ? '' : `/filter?has-image=${!DEFAULT_HAS_IMAGE_VALUE}`;
  const isMilitaryBase = lastSearch.group === MILITARY_BASES_SEARCH_PREDICTION_GROUP;

  let mapViewUrl = '/search';

  let latitude = slugAndCoordinates.latitude;
  let longitude = slugAndCoordinates.longitude;

  if (latitude && longitude) {
    // Default zoom based on location type
    let zoom = 13;
    if (lastSearch.label === AutocompletePredictionPredictionCategory.CITY) {
      zoom = 12;
    } else if (lastSearch.label === AutocompletePredictionPredictionCategory.NEIGHBORHOOD) {
      zoom = 14;
    } else if (lastSearch.label === AutocompletePredictionPredictionCategory.STREET) {
      zoom = 16;
    }

    // Include the areaName in the URL so the search input on map page will be correctly populated
    let areaName = lastSearch.description ? encodeURIComponent(lastSearch.description) : '';

    // Edge case where we have null island (0, 0) coordinates, set them to default values by country
    if (latitude === 0 && longitude === 0) {
      if (isCanadianProvinceCode(isProvinceOrState)) {
        latitude = defaultListingParams.filter.latitude;
        longitude = defaultListingParams.filter.longitude;
        areaName = defaultListingParams.filter.areaName;
      } else {
        latitude = DEFAULT_LISTING_PARAMS_US.filter.latitude;
        longitude = DEFAULT_LISTING_PARAMS_US.filter.longitude;
        areaName = DEFAULT_LISTING_PARAMS_US.filter.areaName;
      }
    }
    mapViewUrl = `/search?latitude=${latitude}&longitude=${longitude}&zoom=${zoom}&area-name=${areaName}`;
  }

  if (isProvinceOrState) {
    const updatedHomeUrl = `${slugAndCoordinates.slug}-real-estate`;
    return {
      homesForSaleUrl: updatedHomeUrl + imageQuery,
      soldPricesUrl: `${updatedHomeUrl}/sold` + imageQuery,
      mapViewUrl,
    };
  } else if (hasAreaPage) {
    // Military base area pages do not have real-estate at the end
    const realEstateSuffix = isMilitaryBase ? '' : '-real-estate';
    const updatedHomeUrl = `/${slugAndCoordinates.slug}${realEstateSuffix}${slugAndCoordinates.subLocation}`;
    return {
      homesForSaleUrl: updatedHomeUrl + imageQuery,
      soldPricesUrl: `${updatedHomeUrl}/sold` + imageQuery,
      mapViewUrl,
    };
  } else {
    return {
      homesForSaleUrl: '/search',
      soldPricesUrl: '/search',
      mapViewUrl,
    };
  }
};

export type ConvertedSearchSuggestion = Partial<SearchPrediction> | SearchSuggestions;

/**
 * Converts a search suggestion object to a search prediction object.
 *
 * @param {SearchSuggestions} suggestion - The search suggestion object to be converted.
 * @returns {ConvertedSearchSuggestion} The search prediction object created from the suggestion.
 *
 */
export async function convertSearchSuggestionToSearchPrediction(
  suggestion: SearchSuggestions
): Promise<ConvertedSearchSuggestion> {
  try {
    const { label = '', urlPath = '', latitude: suggestionLatitude, longitude: suggestionLongitude, identifier = 0 } = suggestion;

    const slug = urlPath.replace('-real-estate', '') || 'unknown-location';
    const id = `${LOCATION_ID_PREFIX}${slug}`;

    const [locationName = 'Unknown Location', locationType = 'city'] = label.split('- ') || [];
    const predictionLabel = locationType.toLowerCase().trim() || 'city';
    const predictionGroup = LOCATIONS_SEARCH_PREDICTION_GROUP;

    let longitude = defaultListingParams?.filter?.longitude;
    let latitude = defaultListingParams?.filter?.latitude;

    if (!suggestionLatitude || !suggestionLongitude) {
      // TODO Remove this call to client-app, need to be replaces with Go-Search Polygon endpoint
      [longitude, latitude] = await getCoordinatesForLocation(slug);
    } else {
      longitude = suggestionLongitude;
      latitude = suggestionLatitude;
    }
    return {
      identifier,
      description: locationName.trim(),
      group: predictionGroup,
      label: predictionLabel,
      id,
      locationId: id,
      urlPath,
      data: {
        position: {
          longitude: longitude,
          latitude: latitude,
          coordinates: [longitude, latitude],
          type: 'Point',
        },
      },
      source: '',
      matchedSubstrings: [],
    } as Partial<SearchPrediction>;
  } catch (error) {
    console.error('Error in converting search suggestion:', error);
    return suggestion;
  }
}
